import { ecartJours } from './calendrier'
import type { MotifCompensation } from './domaine'
import { INTERMEDIAIRES_PAIEMENT } from './justificatifs'
import { estRejet } from './libelle'
import { motsDuMarchand } from './operations-bancaires'
import { MOTS_GENERIQUES } from './recuperation-rattachement'

// Compensation : deux lignes du relevé qui s'annulent (décision de Pierre-Marie du 19/10/2026).
// Un débit et le crédit qui le rend, de même montant et de même devise (paiement annulé ou
// remboursé, débit en double remboursé, prélèvement rejeté, caution rendue) se rapprochent l'un
// de l'autre : aucune facture à chercher pour aucune des deux. Sûre, donc faite seule : même
// compte, même marchand (premiers mots du libellé), à quinze jours au plus, et seules de leur
// espèce de part et d'autre (des lignes jumelles, même jour, même montant, même marchand, valent
// l'une pour l'autre). Même marchand à quatre-vingt-dix jours au plus : proposée à un humain, qui
// peut aussi relier lui-même deux lignes de l'entité de signe contraire et de même montant.

export type LigneACompenser = {
  id: string
  compteId: string
  dateOperation: string
  /** Signé, la ligne entière : une ligne déjà reliée, même en partie, ne se compense pas. */
  montantCts: number
  devise: string
  libelleNormalise: string
}

/** Un débit et le crédit qui l'annule. */
export type PaireCompensee = { debitId: string; creditId: string; motif: MotifCompensation }

/** Une ligne qui peut compenser une autre, pour l'écran de l'opération. */
export type CandidateCompensation = {
  id: string
  memeMarchand: boolean
  /** Jours de la ligne à la candidate (positif quand la candidate suit). */
  ecartJours: number
  motif: MotifCompensation
  /** Même marchand à quatre-vingt-dix jours au plus : proposée. */
  proposee: boolean
}

/** Au plus ce nombre de jours entre les deux lignes pour une compensation faite seule. */
export const JOURS_COMPENSATION_SURE = 15
/** Au plus ce nombre de jours pour une compensation proposée (même marchand). */
export const JOURS_COMPENSATION_PROPOSEE = 90

/** Mots du sens ou du retour (« en votre faveur », « remboursement ») : jamais le marchand. */
const MOTS_DU_RETOUR = new Set([
  'VERS',
  'VOTRE',
  'FAVEUR',
  'MME',
  'MLLE',
  'MADAME',
  'MONSIEUR',
  'REMBOURSEMENT',
  'REMB',
  'REMBT',
  'RBT',
  'ANNULATION',
  'ANNUL',
  'ANNULE',
  'AVOIR',
  'RETOUR',
  'REJET',
  'REJETE',
  'IMPAYE',
  'CAUTION',
  'RESTITUTION',
  'DEPOT',
  'GARANTIE',
])

const CAUTION = /\b(CAUTION|DEPOT DE GARANTIE)\b/

/** Les deux premiers mots qui nomment le marchand, un domaine réduit à sa base. */
function tete(libelleNormalise: string): string[] {
  let mots = motsDuMarchand({ libelle: libelleNormalise, marchand: null })
    .map((m) => (m.includes('.') ? m.split('.')[0]! : m))
    .filter((m) => m.length >= 3 && !MOTS_DU_RETOUR.has(m) && !MOTS_GENERIQUES.has(m.toLowerCase()))
  // Un intermédiaire de paiement en tête (« PAYPAL *ACME ») : le marchand le suit.
  if (mots.length > 1 && INTERMEDIAIRES_PAIEMENT.has(mots[0]!.toLowerCase())) mots = mots.slice(1)
  return mots.slice(0, 2)
}

/** Les motifs dits simplement (commentaires, écrans). */
export const MOTIFS_COMPENSATION_DITS: Readonly<Record<MotifCompensation, string>> = {
  annulation: 'paiement annulé ou remboursé',
  double_debit: 'débit en double remboursé',
  rejet: 'prélèvement rejeté',
  caution: 'caution rendue',
  aller_retour: 'aller-retour',
}

/** Les têtes de deux libellés nomment le même marchand. */
function concordent(ta: readonly string[], tb: readonly string[]): boolean {
  if (ta.length === 0 || tb.length === 0 || ta[0] !== tb[0]) return false
  return ta.length < 2 || tb.length < 2 || ta[1] === tb[1]
}

/**
 * Deux libellés normalisés nomment-ils le même marchand ? Leurs premiers mots concordent, et le
 * deuxième aussi quand les deux en ont un : hors moyen de paiement, carte, date, référence, mots
 * génériques et mots du retour (« remboursement », « en votre faveur »). Une ville qui suit le
 * marchand compte, mais jamais seule.
 */
export function memeMarchand(a: string, b: string): boolean {
  return concordent(tete(a), tete(b))
}

/** Pourquoi ces deux lignes se compensent ; `jumeaux` : un débit parmi des jumeaux. */
export function motifDeCompensation(
  debit: LigneACompenser,
  credit: LigneACompenser,
  jumeaux = false,
): MotifCompensation {
  if (estRejet(credit.libelleNormalise)) return 'rejet'
  if (jumeaux) return 'double_debit'
  if (CAUTION.test(debit.libelleNormalise) || CAUTION.test(credit.libelleNormalise))
    return 'caution'
  return memeMarchand(debit.libelleNormalise, credit.libelleNormalise)
    ? 'annulation'
    : 'aller_retour'
}

/** De signe contraire, même devise, montants opposés non nuls. */
const opposees = (a: LigneACompenser, b: LigneACompenser) =>
  a.id !== b.id &&
  a.montantCts !== 0 &&
  Number.isSafeInteger(a.montantCts) &&
  a.montantCts === -b.montantCts &&
  a.devise === b.devise

/**
 * Les compensations sûres parmi ces lignes encore entières : chaque débit et chaque crédit
 * compatibles (même compte, montants opposés, même devise, même marchand, quinze jours au plus)
 * forment des groupes ; un groupe dont les débits sont jumeaux entre eux (même jour, même
 * marchand) et les crédits aussi se relie deux à deux, le reste attend. Plusieurs débits
 * jumeaux : débit en double remboursé ; le débit relié est toujours le même (identifiant le plus
 * grand, la ligne lue en dernier). Seules les lignes d'un même montant se comparent.
 */
export function compensationsSures(lignes: readonly LigneACompenser[]): PaireCompensee[] {
  const tetes = new Map<string, string[]>()
  const teteDe = (l: LigneACompenser) => {
    let t = tetes.get(l.id)
    if (!t) tetes.set(l.id, (t = tete(l.libelleNormalise)))
    return t
  }
  const meme = (a: LigneACompenser, b: LigneACompenser) => concordent(teteDe(a), teteDe(b))
  const jumelles = (ls: readonly LigneACompenser[]) =>
    ls.every((l) => l.dateOperation === ls[0]!.dateOperation && meme(ls[0]!, l))
  // Même compte, même devise, même montant au signe près.
  const parMontant = new Map<string, LigneACompenser[]>()
  for (const l of lignes) {
    if (l.montantCts === 0 || !Number.isSafeInteger(l.montantCts)) continue
    const cle = `${l.compteId} ${l.devise} ${Math.abs(l.montantCts)}`
    parMontant.set(cle, [...(parMontant.get(cle) ?? []), l])
  }
  const paires: (PaireCompensee & { jour: string })[] = []
  for (const memeMontant of parMontant.values()) {
    const debits = memeMontant.filter((l) => l.montantCts < 0)
    const credits = memeMontant.filter((l) => l.montantCts > 0)
    // Groupes : union-find sur les liens débit-crédit.
    const parent = new Map<string, string>()
    const racine = (id: string): string => {
      const p = parent.get(id) ?? id
      if (p === id) return id
      const r = racine(p)
      parent.set(id, r)
      return r
    }
    const lies = new Set<string>()
    for (const d of debits) {
      for (const c of credits) {
        if (Math.abs(ecartJours(d.dateOperation, c.dateOperation)) > JOURS_COMPENSATION_SURE)
          continue
        if (!meme(d, c)) continue
        // Un rejet suit toujours le prélèvement qu'il rend.
        if (estRejet(c.libelleNormalise) && c.dateOperation < d.dateOperation) continue
        parent.set(racine(d.id), racine(c.id))
        lies.add(d.id).add(c.id)
      }
    }
    const groupes = new Map<string, { debits: LigneACompenser[]; credits: LigneACompenser[] }>()
    for (const l of memeMontant) {
      if (!lies.has(l.id)) continue
      const g = groupes.get(racine(l.id)) ?? { debits: [], credits: [] }
      ;(l.montantCts < 0 ? g.debits : g.credits).push(l)
      groupes.set(racine(l.id), g)
    }
    for (const g of groupes.values()) {
      if (!jumelles(g.debits) || !jumelles(g.credits)) continue
      const lesDebits = [...g.debits].sort((a, b) => (a.id < b.id ? 1 : -1))
      const lesCredits = [...g.credits].sort((a, b) => (a.id < b.id ? -1 : 1))
      const n = Math.min(lesDebits.length, lesCredits.length)
      for (let i = 0; i < n; i++) {
        const [d, c] = [lesDebits[i]!, lesCredits[i]!]
        paires.push({
          debitId: d.id,
          creditId: c.id,
          motif: motifDeCompensation(d, c, g.debits.length > 1),
          jour: d.dateOperation,
        })
      }
    }
  }
  return paires
    .sort((a, b) => a.jour.localeCompare(b.jour) || a.debitId.localeCompare(b.debitId))
    .map(({ jour: _jour, ...p }) => p)
}

/**
 * Les lignes qui peuvent compenser celle-ci : de signe contraire, même montant, même devise,
 * entières. Proposées d'abord (même marchand, quatre-vingt-dix jours au plus), puis le même
 * marchand, puis les plus proches.
 */
export function candidatesCompensation(
  ligne: LigneACompenser,
  autres: readonly LigneACompenser[],
): CandidateCompensation[] {
  return autres
    .filter((a) => opposees(ligne, a))
    .map((a) => {
      const meme = memeMarchand(ligne.libelleNormalise, a.libelleNormalise)
      const ecart = ecartJours(ligne.dateOperation, a.dateOperation)
      const [debit, credit] = ligne.montantCts < 0 ? [ligne, a] : [a, ligne]
      return {
        id: a.id,
        memeMarchand: meme,
        ecartJours: ecart,
        motif: motifDeCompensation(debit, credit),
        proposee: meme && Math.abs(ecart) <= JOURS_COMPENSATION_PROPOSEE,
      }
    })
    .sort(
      (a, b) =>
        Number(b.proposee) - Number(a.proposee) ||
        Number(b.memeMarchand) - Number(a.memeMarchand) ||
        Math.abs(a.ecartJours) - Math.abs(b.ecartJours) ||
        a.id.localeCompare(b.id),
    )
}

/**
 * Parmi ces lignes entières, celles qui ont une compensation proposée (même `groupe`, l'entité
 * de leur compte : signe contraire, même montant, même devise, même marchand, quatre-vingt-dix
 * jours au plus), pour les repérer dans une liste.
 */
export function avecCompensationProposee(
  lignes: readonly (LigneACompenser & { groupe: string })[],
): Set<string> {
  const parMontant = new Map<string, (LigneACompenser & { groupe: string })[]>()
  for (const l of lignes) {
    if (l.montantCts === 0 || !Number.isSafeInteger(l.montantCts)) continue
    const cle = `${l.groupe} ${l.devise} ${Math.abs(l.montantCts)}`
    parMontant.set(cle, [...(parMontant.get(cle) ?? []), l])
  }
  const ids = new Set<string>()
  for (const memeMontant of parMontant.values()) {
    for (const d of memeMontant.filter((l) => l.montantCts < 0)) {
      for (const c of memeMontant.filter((l) => l.montantCts > 0)) {
        if (Math.abs(ecartJours(d.dateOperation, c.dateOperation)) > JOURS_COMPENSATION_PROPOSEE)
          continue
        if (!memeMarchand(d.libelleNormalise, c.libelleNormalise)) continue
        ids.add(d.id).add(c.id)
      }
    }
  }
  return ids
}
